// Day 5 - Full Production Content (PREMIUM)
// Temat: Automatyzacja z AI - szablony i workflow
// Ton: praktyczny, profesjonalny, premium
// Duration: 12-15 minutes

export const day5Content = {
  day_number: 5,
  title: "Automatyzacja z AI",
  subtitle: "Szablony i workflow",
  duration: "12-15 min",
  is_premium: true,
  has_ai_coach: true,
  exercises: [
    // ═══════════════════════════════════════════════════════════════
    // ĆWICZENIE 1: Wprowadzenie do automatyzacji (5 kroków)
    // ═══════════════════════════════════════════════════════════════
    {
      id: "d5_ex1",
      title: "Czym jest automatyzacja z AI?",
      icon: "layers",
      steps: [
        {
          id: "d5_ex1_s1",
          type: "choice",
          title: "Automatyzacja ≠ Zastąpienie",
          content: "Co oznacza automatyzacja z AI w praktyce?",
          options: [
            { id: "a", text: "AI robi wszystko za Ciebie", correct: false },
            { id: "b", text: "AI wykonuje powtarzalne części zadań, Ty kontrolujesz wynik", correct: true },
            { id: "c", text: "Nie musisz już myśleć", correct: false },
            { id: "d", text: "AI zastępuje pracowników", correct: false }
          ],
          shuffle: true,
          softHint: "Automatyzacja to przyspieszenie, nie zastąpienie Twojej pracy.",
          successMessage: "Dokładnie! AI automatyzuje powtarzalne elementy, Ty zachowujesz kontrolę."
        },
        {
          id: "d5_ex1_s2",
          type: "choice",
          title: "Gdzie szukać oszczędności?",
          content: "Które zadania najlepiej nadają się do automatyzacji?",
          options: [
            { id: "a", text: "Zadania wymagające kreatywności", correct: false },
            { id: "b", text: "Zadania powtarzalne o podobnej strukturze", correct: true },
            { id: "c", text: "Zadania wymagające empatii", correct: false },
            { id: "d", text: "Wszystkie zadania bez wyjątku", correct: false }
          ],
          shuffle: true,
          softHint: "Szukaj wzorców – jeśli robisz coś wielokrotnie podobnie, to kandydat do automatyzacji.",
          successMessage: "Tak! Powtarzalne zadania o stałej strukturze to idealni kandydaci."
        },
        {
          id: "d5_ex1_s3",
          type: "classify",
          title: "Automatyzować czy nie?",
          content: "Przyporządkuj zadania:",
          categories: ["Automatyzuj", "Rób ręcznie"],
          items: [
            { id: "c1", text: "Pisanie 10 podobnych emaili", category: "Automatyzuj" },
            { id: "c2", text: "Negocjacje z klientem", category: "Rób ręcznie" },
            { id: "c3", text: "Tworzenie raportów tygodniowych", category: "Automatyzuj" },
            { id: "c4", text: "Rozwiązywanie konfliktów w zespole", category: "Rób ręcznie" },
            { id: "c5", text: "Podsumowania spotkań", category: "Automatyzuj" },
            { id: "c6", text: "Budowanie strategii firmy", category: "Rób ręcznie" }
          ],
          softHint: "Automatyzuj powtarzalne, zachowaj ludzkie dla relacji i strategii.",
          successMessage: "Świetnie! Wiesz już, gdzie AI daje największą wartość."
        },
        {
          id: "d5_ex1_s4",
          type: "choice",
          title: "ROI automatyzacji",
          content: "Kiedy automatyzacja ma sens ekonomiczny?",
          options: [
            { id: "a", text: "Zawsze, bez wyjątku", correct: false },
            { id: "b", text: "Gdy czas na stworzenie szablonu < czas zaoszczędzony na wielokrotnym użyciu", correct: true },
            { id: "c", text: "Tylko dla dużych firm", correct: false },
            { id: "d", text: "Gdy masz dużo wolnego czasu", correct: false }
          ],
          shuffle: true,
          softHint: "Pomyśl: ile czasu zajmie stworzenie szablonu vs ile zaoszczędzisz używając go 10 razy?",
          successMessage: "Tak! Jeśli szablon zaoszczędzi więcej czasu niż kosztuje jego stworzenie – warto."
        },
        {
          id: "d5_ex1_s5",
          type: "fill",
          title: "Złota zasada",
          content: "Dokończ zdanie:",
          template: "Jeśli robię coś więcej niż _______ razy, tworzę szablon.",
          correctAnswers: ["3", "trzy", "2", "dwa", "kilka", "parę"],
          minLength: 1,
          softHint: "Zazwyczaj 2-3 powtórzenia to sygnał do stworzenia szablonu.",
          successMessage: "Dokładnie! Zasada 3 powtórzeń – idealny moment na szablon."
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // ĆWICZENIE 2: Anatomia dobrego szablonu (6 kroków)
    // ═══════════════════════════════════════════════════════════════
    {
      id: "d5_ex2",
      title: "Anatomia dobrego szablonu",
      icon: "edit",
      steps: [
        {
          id: "d5_ex2_s1",
          type: "choice",
          title: "Co to jest szablon promptu?",
          content: "Szablon to:",
          options: [
            { id: "a", text: "Gotowy prompt bez możliwości zmian", correct: false },
            { id: "b", text: "Prompt z miejscami do uzupełnienia (zmiennymi)", correct: true },
            { id: "c", text: "Lista wszystkich promptów", correct: false },
            { id: "d", text: "Instrukcja dla AI", correct: false }
          ],
          shuffle: true,
          softHint: "Szablon ma stałe elementy i zmienne do uzupełnienia.",
          successMessage: "Tak! Szablon = stała struktura + zmienne [PLACEHOLDER]."
        },
        {
          id: "d5_ex2_s2",
          type: "choice",
          title: "Elementy szablonu",
          content: "Które elementy powinien zawierać dobry szablon?",
          options: [
            { id: "a", text: "Tylko treść do wygenerowania", correct: false },
            { id: "b", text: "Rolę, zadanie, kontekst (zmienne), format wyjścia", correct: true },
            { id: "c", text: "Jak najwięcej tekstu", correct: false },
            { id: "d", text: "Tylko zmienne do uzupełnienia", correct: false }
          ],
          shuffle: true,
          softHint: "Pamiętaj formułę RZKF z poprzednich dni.",
          successMessage: "Właśnie! RZKF + zmienne [PLACEHOLDER] = dobry szablon."
        },
        {
          id: "d5_ex2_s3",
          type: "choice",
          title: "Przykład szablonu emaila",
          content: "Który szablon jest lepszy?",
          options: [
            { id: "a", text: "\"Napisz email do [ODBIORCA] w sprawie [TEMAT]\"", correct: false },
            { id: "b", text: "\"Jako asystent biznesowy, napisz profesjonalny email do [ODBIORCA] w sprawie [TEMAT]. Ton: [TON]. Długość: [DŁUGOŚĆ]. Uwzględnij: [KLUCZOWE_PUNKTY].\"", correct: true },
            { id: "c", text: "\"Email proszę\"", correct: false },
            { id: "d", text: "\"Napisz coś do klienta\"", correct: false }
          ],
          shuffle: true,
          softHint: "Dobry szablon ma wszystkie elementy RZKF i jasne zmienne.",
          successMessage: "Dokładnie! Ten szablon jest kompletny i elastyczny."
        },
        {
          id: "d5_ex2_s4",
          type: "shortPrompt",
          title: "Stwórz szablon",
          content: "Napisz szablon promptu do tworzenia podsumowań spotkań. Użyj zmiennych w formacie [NAZWA]:",
          placeholder: "Jako [ROLA], stwórz podsumowanie spotkania...",
          minLength: 80,
          requiredElements: ["[", "]", "spotkani|meeting", "podsumowan|summary"],
          softHint: "Uwzględnij: rolę, cel, zmienne (uczestnicy, data, tematy), format wyjścia.",
          successMessage: "Świetny szablon! Teraz możesz go używać wielokrotnie."
        },
        {
          id: "d5_ex2_s5",
          type: "choice",
          title: "Testowanie szablonu",
          content: "Jak sprawdzić, czy szablon działa dobrze?",
          options: [
            { id: "a", text: "Wystarczy stworzyć i od razu używać", correct: false },
            { id: "b", text: "Przetestować z różnymi wartościami zmiennych i poprawić", correct: true },
            { id: "c", text: "Zapytać AI czy jest dobry", correct: false },
            { id: "d", text: "Nie trzeba testować", correct: false }
          ],
          shuffle: true,
          softHint: "Każdy szablon wymaga testowania z różnymi danymi wejściowymi.",
          successMessage: "Tak! Test z różnymi wartościami pokaże słabe punkty szablonu."
        },
        {
          id: "d5_ex2_s6",
          type: "reorder",
          title: "Proces tworzenia szablonu",
          content: "Ułóż kroki tworzenia szablonu:",
          items: [
            { id: "r1", text: "Zidentyfikuj powtarzalne zadanie", order: 1 },
            { id: "r2", text: "Napisz prompt dla jednego przypadku", order: 2 },
            { id: "r3", text: "Zamień zmienne części na [PLACEHOLDER]", order: 3 },
            { id: "r4", text: "Przetestuj z różnymi wartościami", order: 4 },
            { id: "r5", text: "Popraw i zapisz szablon", order: 5 }
          ],
          softHint: "Zacznij od konkretnego przypadku, potem uogólnij.",
          successMessage: "To jest profesjonalny proces tworzenia szablonów!"
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // ĆWICZENIE 3: Workflow z AI (6 kroków)
    // ═══════════════════════════════════════════════════════════════
    {
      id: "d5_ex3",
      title: "Budowanie workflow",
      icon: "target",
      steps: [
        {
          id: "d5_ex3_s1",
          type: "choice",
          title: "Co to jest workflow AI?",
          content: "Workflow to:",
          options: [
            { id: "a", text: "Pojedynczy prompt", correct: false },
            { id: "b", text: "Sekwencja połączonych kroków (promptów) prowadzących do celu", correct: true },
            { id: "c", text: "Lista wszystkich szablonów", correct: false },
            { id: "d", text: "Automatyczny bot", correct: false }
          ],
          shuffle: true,
          softHint: "Workflow to przepływ pracy – wiele kroków połączonych w proces.",
          successMessage: "Tak! Workflow = sekwencja kroków do osiągnięcia złożonego celu."
        },
        {
          id: "d5_ex3_s2",
          type: "choice",
          title: "Przykład: Tworzenie posta na bloga",
          content: "Który workflow jest najlepszy dla napisania posta?",
          options: [
            { id: "a", text: "Jeden prompt: 'Napisz post o AI'", correct: false },
            { id: "b", text: "1. Research tematu → 2. Outline → 3. Pisanie sekcji → 4. Edycja → 5. SEO", correct: true },
            { id: "c", text: "Pisanie całości na raz bez planu", correct: false },
            { id: "d", text: "Kopiowanie cudzych postów", correct: false }
          ],
          shuffle: true,
          softHint: "Złożone zadania lepiej dzielić na mniejsze kroki.",
          successMessage: "Właśnie! Podział na etapy daje lepszą kontrolę i jakość."
        },
        {
          id: "d5_ex3_s3",
          type: "reorder",
          title: "Workflow: Analiza konkurencji",
          content: "Ułóż etapy workflow analizy konkurencji:",
          items: [
            { id: "r1", text: "Zbierz dane o konkurentach", order: 1 },
            { id: "r2", text: "Przeanalizuj mocne strony każdego", order: 2 },
            { id: "r3", text: "Zidentyfikuj słabe punkty", order: 3 },
            { id: "r4", text: "Porównaj z własną ofertą", order: 4 },
            { id: "r5", text: "Stwórz rekomendacje działań", order: 5 }
          ],
          softHint: "Logiczny przepływ: dane → analiza → wnioski → działania.",
          successMessage: "Świetny workflow analityczny!"
        },
        {
          id: "d5_ex3_s4",
          type: "choice",
          title: "Łączenie kroków",
          content: "Jak przekazywać wyniki między krokami workflow?",
          options: [
            { id: "a", text: "Każdy krok od zera, bez kontekstu", correct: false },
            { id: "b", text: "Wynik poprzedniego kroku staje się inputem następnego", correct: true },
            { id: "c", text: "Wszystko w jednym długim prompcie", correct: false },
            { id: "d", text: "Losowo, jak wyjdzie", correct: false }
          ],
          shuffle: true,
          softHint: "Output kroku N → Input kroku N+1",
          successMessage: "Tak! Każdy krok buduje na wyniku poprzedniego."
        },
        {
          id: "d5_ex3_s5",
          type: "shortPrompt",
          title: "Zaprojektuj workflow",
          content: "Opisz 4-5 kroków workflow dla: 'Przygotowanie prezentacji dla klienta':",
          placeholder: "1. Zbierz informacje o kliencie\n2. ...",
          minLength: 100,
          requiredElements: ["1", "2", "3", "prezentacj|klient"],
          softHint: "Pomyśl: research → struktura → treść → wizualizacja → sprawdzenie",
          successMessage: "Świetny workflow! Teraz możesz go zautomatyzować."
        },
        {
          id: "d5_ex3_s6",
          type: "choice",
          title: "Optymalizacja workflow",
          content: "Kiedy warto uprosić workflow?",
          options: [
            { id: "a", text: "Nigdy, więcej kroków = lepiej", correct: false },
            { id: "b", text: "Gdy niektóre kroki można połączyć bez utraty jakości", correct: true },
            { id: "c", text: "Zawsze, im mniej kroków tym lepiej", correct: false },
            { id: "d", text: "Gdy nie masz czasu", correct: false }
          ],
          shuffle: true,
          softHint: "Balans: wystarczająco dużo kroków dla kontroli, nie za dużo dla efektywności.",
          successMessage: "Dokładnie! Optymalizuj – łącz kroki gdzie to możliwe bez utraty jakości."
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // ĆWICZENIE 4: Praktyczne zastosowania (5 kroków)
    // ═══════════════════════════════════════════════════════════════
    {
      id: "d5_ex4",
      title: "Praktyczne zastosowania",
      icon: "briefcase",
      steps: [
        {
          id: "d5_ex4_s1",
          type: "choice",
          title: "Automatyzacja emaili",
          content: "Który scenariusz emailowy najlepiej automatyzować?",
          options: [
            { id: "a", text: "Odpowiedź na skargę klienta", correct: false },
            { id: "b", text: "Standardowe potwierdzenia, przypomnienia, follow-upy", correct: true },
            { id: "c", text: "Negocjacje cenowe", correct: false },
            { id: "d", text: "Przeprosiny za błąd firmy", correct: false }
          ],
          shuffle: true,
          softHint: "Automatyzuj standardowe, zachowaj ręcznie dla delikatnych spraw.",
          successMessage: "Tak! Standardowe emaile to idealni kandydaci do automatyzacji."
        },
        {
          id: "d5_ex4_s2",
          type: "choice",
          title: "Automatyzacja raportów",
          content: "Jak najlepiej automatyzować raporty tygodniowe?",
          options: [
            { id: "a", text: "Pisać od zera każdy tydzień", correct: false },
            { id: "b", text: "Stworzyć szablon ze stałą strukturą i zmiennymi dla danych", correct: true },
            { id: "c", text: "Kopiować poprzedni raport", correct: false },
            { id: "d", text: "Nie robić raportów", correct: false }
          ],
          shuffle: true,
          softHint: "Stała struktura + zmienne dane = efektywny szablon raportu.",
          successMessage: "Dokładnie! Szablon z sekcjami i miejscami na dane = szybki raport."
        },
        {
          id: "d5_ex4_s3",
          type: "classify",
          title: "Rodzaje automatyzacji",
          content: "Przyporządkuj do typu automatyzacji:",
          categories: ["Szablon jednorazowy", "Workflow wieloetapowy"],
          items: [
            { id: "c1", text: "Email potwierdzający spotkanie", category: "Szablon jednorazowy" },
            { id: "c2", text: "Tworzenie strategii marketingowej", category: "Workflow wieloetapowy" },
            { id: "c3", text: "Odpowiedź na FAQ", category: "Szablon jednorazowy" },
            { id: "c4", text: "Analiza i raport z kampanii", category: "Workflow wieloetapowy" },
            { id: "c5", text: "Podsumowanie spotkania", category: "Szablon jednorazowy" },
            { id: "c6", text: "Onboarding nowego pracownika", category: "Workflow wieloetapowy" }
          ],
          softHint: "Proste = szablon, złożone = workflow.",
          successMessage: "Świetnie! Dopasujesz narzędzie do zadania."
        },
        {
          id: "d5_ex4_s4",
          type: "choice",
          title: "Twoja biblioteka szablonów",
          content: "Gdzie najlepiej przechowywać szablony?",
          options: [
            { id: "a", text: "W pamięci", correct: false },
            { id: "b", text: "W zorganizowanym dokumencie/narzędziu z kategoriami", correct: true },
            { id: "c", text: "Losowo w różnych miejscach", correct: false },
            { id: "d", text: "Tworzę od nowa za każdym razem", correct: false }
          ],
          shuffle: true,
          softHint: "Organizacja oszczędza czas – łatwo znaleźć = łatwo użyć.",
          successMessage: "Tak! Zorganizowana biblioteka szablonów to klucz do efektywności."
        },
        {
          id: "d5_ex4_s5",
          type: "choice",
          title: "Plan na jutro",
          content: "Co zrobisz jako pierwsze po tej lekcji?",
          options: [
            { id: "a", text: "Zidentyfikuję jedno powtarzalne zadanie i stworzę szablon", isChoice: true },
            { id: "b", text: "Przejrzę swoją pracę pod kątem automatyzacji", isChoice: true },
            { id: "c", text: "Stworzę folder na szablony", isChoice: true },
            { id: "d", text: "Zaprojektuję workflow dla złożonego zadania", isChoice: true }
          ],
          shuffle: false,
          softHint: "Każda opcja jest dobra – wybierz to, co najbardziej Ci pomoże.",
          successMessage: "Świetny plan! Działaj i buduj swoją bibliotekę automatyzacji."
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // ĆWICZENIE 5: Podsumowanie (3 kroki)
    // ═══════════════════════════════════════════════════════════════
    {
      id: "d5_ex5",
      title: "Podsumowanie",
      icon: "trophy",
      steps: [
        {
          id: "d5_ex5_s1",
          type: "choice",
          title: "Quiz: Szablon vs Workflow",
          content: "Kiedy użyć szablonu, a kiedy workflow?",
          options: [
            { id: "a", text: "Szablon: proste, powtarzalne. Workflow: złożone, wieloetapowe.", correct: true },
            { id: "b", text: "Zawsze workflow, bo lepszy", correct: false },
            { id: "c", text: "Zawsze szablon, bo prostszy", correct: false },
            { id: "d", text: "Losowo, jak wyjdzie", correct: false }
          ],
          shuffle: true,
          softHint: "Dopasuj narzędzie do złożoności zadania.",
          successMessage: "Dokładnie! Proste → szablon, złożone → workflow."
        },
        {
          id: "d5_ex5_s2",
          type: "choice",
          title: "Quiz: Elementy szablonu",
          content: "Co MUSI zawierać dobry szablon?",
          options: [
            { id: "a", text: "Tylko zmienne [PLACEHOLDER]", correct: false },
            { id: "b", text: "RZKF (Rola, Zadanie, Kontekst, Format) + zmienne", correct: true },
            { id: "c", text: "Tylko długi tekst", correct: false },
            { id: "d", text: "Magiczne słowa", correct: false }
          ],
          shuffle: true,
          softHint: "Formuła RZKF + miejsca na zmienne dane.",
          successMessage: "Tak! RZKF + [PLACEHOLDERS] = kompletny szablon."
        },
        {
          id: "d5_ex5_s3",
          type: "choice",
          title: "Dzień 5 ukończony!",
          content: "Co dalej?",
          options: [
            { id: "a", text: "Wróć do listy lekcji", isChoice: true },
            { id: "b", text: "Wypróbuj AI Coacha", isChoice: true }
          ],
          shuffle: false,
          softHint: "Wybierz swoją ścieżkę!",
          successMessage: "Dzień 5 za Tobą! Umiesz już automatyzować pracę z AI. Jutro: zaawansowane techniki!"
        }
      ]
    }
  ]
};

export default day5Content;
